import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import { useTenantContext } from '../components/App/AppTenant';
import EqxPdfViewer from '../components/EqxPdfViewer';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import { PayslipModel } from '../defs/defs';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import EqxError from '../shared/components/EqxError';
import EqxIcon from '../shared/components/EqxIcon';
import EqxLoading from '../shared/components/EqxLoading';
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler';
import EqxTile, { EqxTileBlank } from '../shared/components/EqxTile/EqxTile';
import EqxTileGroup from '../shared/components/EqxTile/EqxTileGroup';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';
import { formatDate } from '../shared/utils/formatDate';

const PayslipDocument = (props: { payslip: PayslipModel }) => {
    const { payslip } = props;
    const api = useApi();
    const [content, setContent] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchDocument = async () => {
            try {
                setLoading(true);
                setError(null);
                const documentContent = await api.fetchBinary(api.endpoints.payslipDocument(payslip.id).url);
                setContent(documentContent);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load document');
            } finally {
                setLoading(false);
            }
        };

        fetchDocument();
    }, [api, payslip.id]);

    return (
        <EqxRequestHandler
            state={{ loading, error: error ?? null }}
            isEmpty={!content}
            empty={<div className="p-5 text-center text-muted">Document not available<br /><EqxIcon fa="fas fa-times" /></div>}
        >
            <EqxPdfViewer content={content!} />
        </EqxRequestHandler>
    );
};

const PayslipActions = (props: { payslip: PayslipModel }) => {
    const { payslip } = props;
    const { isOffline } = useOffline();

    return (
        <EqxTileGroup size="xs">
            <EqxTile
                className='col-6'
                title={payslip.documentURL ? "Download Payslip" : "File not available"}
                icon="fas fa-download"
                color="primary"
                disabled={isOffline || !payslip.documentURL}
                link={payslip.documentURL || "#"}
                target={payslip.documentURL ? "_blank" : undefined}
            />
            <EqxTileBlank className="col-6" />
        </EqxTileGroup>
    );
};

// Main content component extracted from PagePayslipDetail
const PayslipDetailContent = () => {
    const api = useApi()
    const { id } = useParams<{ id: string }>()

    const { response: payslip, loading, error } = useApiEndpoint<PayslipModel>(
        api,
        api.endpoints.payslip(id!)
    )
    const tenant = useTenantContext()
    const mainContext = useMainLayoutContext()

    if (!mainContext.permissions?.canViewPayslips) {
        return <EqxError error="You do not have permission to view payslips." />
    }
    if (!tenant.isManagedPayroll) {
        return <EqxError error="Managed Payroll is not enabled for this business" />;
    }
    if (!mainContext.user?.staffID) {
        if (!mainContext.user) {
            return <EqxLoading />
        } else {
            return <EqxError error="Only staff can view payslips" />;
        }
    }

    return (
        <EqxRequestHandler
            state={{ loading, error: error ?? null }}
            isEmpty={!payslip}
            empty={<div className="p-5 text-center text-muted">Payslip not found</div>}
            content={() => {
                const slip = payslip!;

                return <>
                    <EqxCard
                        isSection
                        collapsible={false}
                        className="mb-3"
                        noPadding={true}
                        header={
                            <div>
                                <h5 className="mb-0">{slip.displayNameForStaff || slip.externalRef || `Payslip #${slip.id}`}</h5>
                                {slip.periodStartDate && slip.periodFinishDate && (
                                    <div className="text-muted">
                                        {formatDate(slip.periodStartDate)} - {formatDate(slip.periodFinishDate)}
                                    </div>
                                )}
                            </div>
                        }
                        footer={
                            <div className="d-flex justify-content-between">
                                {slip.grossAmount && (
                                    <dl className="m-0">
                                        <dt>Gross</dt>
                                        <dd>&pound;{slip.grossAmount.toFixed(2)}</dd>
                                    </dl>
                                )}
                                {slip.netAmount && (
                                    <dl className="m-0">
                                        <dt>Net</dt>
                                        <dd className="text-success">&pound;{slip.netAmount.toFixed(2)}</dd>
                                    </dl>
                                )}
                            </div>
                        }

                    >
                        {payslip?.documentURL ? (
                            <PayslipDocument payslip={payslip} />
                        ) : (
                            <div className="p-5 text-center text-muted">Document not available<br /><EqxIcon fa="fas fa-times" /></div>
                        )}
                    </EqxCard>

                    <PayslipActions payslip={slip} />
                </>
            }}
        >
        </EqxRequestHandler>
    )
}

const PagePayslipDetail = () => {
    return (
        <MainLayout
            pageTitle="Payslip Details"
            relatedLinks={[
                { url: '/payslips', text: 'Back to Payslips' }
            ]}
        >
            <PayslipDetailContent />
        </MainLayout>
    )
}

export default PagePayslipDetail 